<template>
	<Dialog
		:dialog-visible="dialog"
		:title="dialogTitle"
		:agree-text="buttonText"
		:cancel-text="cancelText"
		:disable-agree-button="createButtonDisabled"
		:options="$props.options"
		@cancel="cancel"
		@agree="agree">
		<template #dialog-body>
			<div v-if="mode === 'duplicate-confirm' && duplicateMatch" v-test="'duplicate-contact-confirm'">
				<p class="mb-3">A contact with that {{ duplicateMatchedField }} already exists. Use the existing contact?</p>
				<div class="pa-3 rounded-lg bg-grey-lighten-4">
					<div class="font-weight-bold">{{ duplicateMatchSummary.name }}</div>
					<div v-if="duplicateMatchSummary.email" class="text-small">{{ duplicateMatchSummary.email }}</div>
					<div v-if="duplicateMatchSummary.phone" class="text-small">{{ duplicateMatchSummary.phone }}</div>
				</div>
			</div>
			<v-form v-else ref="form" v-model="isFormValid">
				<CharacterLimitedTextField
					v-model.trim="customer.firstname"
					field-noun="first name"
					:max-length="FIELD_LENGTHS.NAME"
					:rules="firstNameRules">
					<template #label><FieldLabel label="First name (Optional)" /></template>
				</CharacterLimitedTextField>
				<CharacterLimitedTextField
					v-model.trim="customer.lastname"
					field-noun="last name"
					:max-length="FIELD_LENGTHS.NAME"
					validate-on="blur"
					:rules="lastNameRules">
					<template #label><FieldLabel label="Last name (Optional)" /></template>
				</CharacterLimitedTextField>
				<CharacterLimitedTextField
					v-model.trim="customer.companyName"
					field-noun="company name"
					:rules="companyNameRules"
					:max-length="COMPANY_NAME_MAX_LENGTH">
					<template #label><FieldLabel label="Company name (Optional)" /></template>
				</CharacterLimitedTextField>
				<EmailTextField
					v-model="emailAddress"
					:rules="emailRules"
					:max-length="FIELD_LENGTHS.EMAIL"
					invalid-message="Please enter a valid email address.">
					<template #label><FieldLabel :label="`Email address ${emailMandatoryText ? '(Required)' : '(Optional)'}`" /></template>
				</EmailTextField>
				<div @paste.prevent="pasteEvent">
					<vue-tel-input
						v-bind="vueTelProps"
						v-model="phoneNumber"
						valid-color="#000000"
						:style="{
							height: '54px',
							marginBottom: !validPhone && showPhoneNumberFieldErrors ? '8px' : '0',
							border: showPhoneNumberFieldErrors && !validPhone ? '2px solid red !important' : ''
						}"
						@input="phoneNumberChanged"
						@validate="validatePhoneNumber"
						@blur="phoneNumberBlur"
						@focus="phoneNumberFocus" />
				</div>
				<div v-if="showPhoneNumberFieldErrors && !validPhone" class="v-text-field__details" style="padding: 0 12px">
					<div class="v-messages theme--light error--text" role="alert">
						<div class="v-messages__wrapper">
							<div class="v-messages__message">Please enter a valid phone number.</div>
						</div>
					</div>
				</div>
			</v-form>
		</template>
	</Dialog>
</template>

<script>
	import api from "../api";
	import {
		addEmailToCustomer,
		addPhoneNumberToCustomer,
		calculateTimeout,
		getMessageFromError,
		getTrimmedPhoneFromClipboard
	} from "@/helpers/utilities";
	import { couldNot } from "@/helpers/userMessages";
	import Dialog from "./Dialog.vue";
	import { FIELD_LENGTHS, validateFormOrWarn } from "@/helpers/Validation";
	import { getVueTelProps } from "@/helpers/phone";
	import { SafeVueTelInput as VueTelInput } from "@/components/SafeVueTelInput";
	import { useToast } from "vue-toastification";
	import { COMPANY_NAME_MAX_LENGTH } from "@/api/constants";
	import { getCustomerDisplayName } from "@/formatters/customers";
	import { getCustomerPhoneDedupWarningMessage } from "@/helpers/customerCreateWarnings";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	export default {
		components: {
			FieldLabel,
			Dialog,
			VueTelInput
		},
		setup() {
			const toast = useToast();
			return { toast, FIELD_LENGTHS, COMPANY_NAME_MAX_LENGTH };
		},
		props: {
			options: {
				type: Object,
				default: () => ({
					width: "500px"
				})
			}
		},
		data() {
			return {
				dialog: false,
				resolve: null,
				reject: null,
				form: false,
				message: null,
				customer: {
					firstname: "",
					lastname: "",
					companyName: "",
					EmailAddresses: [],
					PhoneNumbers: []
				},
				initialCustomer: null,
				emailAddress: "",
				phoneNumber: "",
				validPhone: false,
				vueTelProps: getVueTelProps(),
				creating: false,
				emailMandatoryText: null,
				isEditing: false,
				showPhoneNumberFieldErrors: false,
				isFormValid: false,
				mode: "form",
				duplicateMatch: null,
				duplicateMatchedField: "",
				sessionId: 0
			};
		},
		computed: {
			dialogTitle() {
				if (this.mode === "duplicate-confirm") {
					return "Contact already exists";
				}
				return this.isEditing ? "Update details" : "Create New Contact";
			},
			cancelText() {
				return this.mode === "duplicate-confirm" ? "Go Back" : "Cancel";
			},
			duplicateMatchSummary() {
				const match = this.duplicateMatch;
				if (!match) return { name: "", email: "", phone: "" };
				return {
					name: getCustomerDisplayName(match) || "Existing contact",
					email: match.EmailAddresses?.[0]?.emailAddress || "",
					phone: match.PhoneNumbers?.[0]?.phoneNumber || ""
				};
			},
			buttonText() {
				if (this.mode === "duplicate-confirm") {
					return "Use existing";
				}
				if (this.isEditing) {
					return this.creating ? "Updating" : "Update";
				}
				return this.creating ? "Creating" : "Create";
			},
			isDirty() {
				if (!this.isEditing) {
					return false;
				}

				const isDirty =
					this.customer.firstname !== this.initialCustomer.firstname ||
					this.customer.lastname !== this.initialCustomer.lastname ||
					this.customer.companyName !== this.initialCustomer.companyName ||
					this.emailAddress !== this.initialCustomer.EmailAddresses[0]?.emailAddress ||
					this.phoneNumber !== this.initialCustomer.PhoneNumbers[0]?.phoneNumber;
				return isDirty;
			},
			// FIELD_LENGTHS.NAME (45 code points) is now enforced by CharacterLimitedTextField's
			// :max-length (LSP-16852). No server-unit backstop needed (LSP-17482): contact-api's
			// createCustomer/updateCustomer/mergeCustomers (customers.ts:298/:301, :463/:466, :982/:985)
			// now count CODE POINTS too, matching this field's own limit exactly.
			firstNameRules() {
				return [];
			},
			lastNameRules() {
				return [];
			},

			// COMPANY_NAME_MAX_LENGTH (50 code points) is now enforced by CharacterLimitedTextField's
			// :max-length (LSP-16852). No server-unit backstop needed: no contact-api validator checks
			// companyName length narrower than the Customer model's default varchar(255) column, and 50
			// code points is at most 100 UTF-16 units — under that column width.
			companyNameRules() {
				return [];
			},
			emailRules() {
				const rules = [];
				if (this.emailMandatoryText) {
					rules.push((v) => !!v || this.emailMandatoryText);
				}
				// FIELD_LENGTHS.EMAIL (254 ASCII characters) is enforced by EmailTextField's
				// :max-length contract. contact-api enforces the same Postmark-compatible ceiling.
				return rules;
			},
			// PR #3271 review — Finding 2: previously disabled Agree in edit mode when !isFormValid ||
			// !validPhone — the same always-enabled-button anti-pattern already removed from EditUserDialog
			// and WelcomeWizardDialog (root CLAUDE.md, "Form action buttons — always enabled, no-op when
			// invalid"). Both validity terms moved into agree()'s guard below via validateFormOrWarn, so
			// they now gate identically in create AND edit mode instead of edit-only. `mode ===
			// "duplicate-confirm"` is a UI-mode branch (no v-form is even rendered in that step), not a
			// validity or in-flight term, so it stays. `creating` is the genuinely in-flight async-submit
			// term (prevents a double-submit) and also stays.
			createButtonDisabled() {
				if (this.mode === "duplicate-confirm") {
					return false;
				}
				return this.creating;
			}
		},
		watch: {
			emailAddress(newEmailAddress) {
				if (!newEmailAddress?.trim()) {
					this.customer.EmailAddresses = [];
				}
			}
		},
		async mounted() {},
		methods: {
			open(input, emailMandatoryText = null) {
				this.sessionId++;
				this.isEditing = false;
				this.emailMandatoryText = emailMandatoryText;
				this.creating = false;
				this.resetDuplicateConfirm();
				this.dialog = true;
				this.resetForm(input);
				return new Promise((resolve, reject) => {
					this.resolve = resolve;
					this.reject = reject;
				});
			},
			async editContact(customerId, emailMandatoryText = null) {
				try {
					this.sessionId++;
					// First get the customer data
					const customer = await api.getCustomer(customerId);
					// Set up the dialog with customer data
					this.initialCustomer = { ...customer };
					this.customer = customer;
					this.phoneNumber = customer.PhoneNumbers[0]?.phoneNumber;
					this.emailAddress = customer.EmailAddresses[0]?.emailAddress;
					this.creating = false;
					this.resetDuplicateConfirm();
					this.dialog = true;
					this.isEditing = true;
					this.emailMandatoryText = emailMandatoryText;

					// Return the promise that will resolve when dialog is completed
					return new Promise((resolve, reject) => {
						this.resolve = resolve;
						this.reject = reject;
					});
				} catch (err) {
					console.error("Error fetching customer:", err);
					throw err;
				}
			},
			resetForm(input) {
				this.$nextTick(() => {
					this.customer.EmailAddresses = [];
					this.customer.PhoneNumbers = [];
					this.phoneNumber = "";
					this.emailAddress = "";
					this.customer.firstname = "";
					this.customer.lastname = "";
					this.customer.companyName = "";
					this.showPhoneNumberFieldErrors = false;
					if (input) {
						// Check if input is all digits (optionally with a leading +)
						if (input.match(/^\+?[\d]+$/)) {
							this.phoneNumber = input;
						} else {
							const trimmedName = input.trim();
							if (trimmedName.includes(" ")) {
								this.customer = {
									...this.customer,
									firstname: trimmedName.split(" ")[0],
									lastname: trimmedName.split(" ").slice(1).join(" ")
								};
							} else {
								this.customer = { ...this.customer, firstname: trimmedName };
							}
						}
					}
					this.$refs.form?.resetValidation();
				});
			},
			async agree() {
				if (this.mode === "duplicate-confirm") {
					const match = this.duplicateMatch;
					// Close before resolving so parent resumes after overlay teardown.
					this.resetDuplicateConfirm();
					this.resetForm("");
					this.dialog = false;
					const resolveDuplicate = this.resolve;
					this.resolve = null;
					await this.$nextTick();
					if (resolveDuplicate) resolveDuplicate(match);
					return;
				}
				try {
					this.showPhoneNumberFieldErrors = true;
					// PR #3271 review — Finding 2: routes through the shared validateFormOrWarn helper (same
					// convention as ReferAFriendDialog.vue and OrganizationDetails.vue) instead of a raw
					// `this.$refs.form.validate()` call, so an unmounted form ref warns distinctly from a
					// form that genuinely failed validation. createButtonDisabled no longer disables Agree on
					// !isFormValid/!validPhone in edit mode (root CLAUDE.md, "Form action buttons — always
					// enabled, no-op when invalid"), so this guard is now the ONLY thing blocking a click on
					// an invalid edit — it must actually validate, not read a reactive flag (the
					// WelcomeWizardDialog bug this PR already hit once).
					const validationResult = await validateFormOrWarn(
						this.$refs.form,
						"[CreateCustomerDialog] agree: form ref not mounted, cannot validate"
					);
					if (!validationResult) {
						return;
					}
					if (!validationResult.valid || !this.validPhone) {
						console.warn("CreateCustomerDialog.agree: blocked — form failed validation, not saving", {
							isEditing: this.isEditing,
							customerId: this.customer.id ?? null,
							validPhone: this.validPhone
						});
						return;
					}
					this.creating = true;
					const currentEmailAddress = this.emailAddress?.trim() || "";
					if (currentEmailAddress) {
						this.customer = addEmailToCustomer(this.customer, currentEmailAddress);
					} else {
						this.customer.EmailAddresses = [];
					}
					this.customer = addPhoneNumberToCustomer(this.customer, this.phoneNumber);
					const warnings = [];
					const customerPayload = {
						...this.customer,
						EmailAddresses: [...this.customer.EmailAddresses],
						PhoneNumbers: [...this.customer.PhoneNumbers]
					};
					const isCreatingCustomer = !this.isEditing;
					const record = this.isEditing
						? await api.updateCustomer(this.customer.id, customerPayload)
						: await api.createCustomer(customerPayload, warnings);
					let customer = record;
					try {
						customer = await api.getCustomer(record.id);
					} catch (enrichErr) {
						// Enrichment fetch — don't fail the dialog if create already succeeded.
						console.error(enrichErr, {
							tags: { method: "agree.getCustomer", file: "CreateCustomerDialog" }
						});
					}
					this.resolve(customer);
					this.resetForm("");
					this.dialog = false;
					const duplicatePhoneWarningMessage = isCreatingCustomer ? getCustomerPhoneDedupWarningMessage(warnings) : "";
					if (duplicatePhoneWarningMessage) {
						this.toast.warning(duplicatePhoneWarningMessage, { timeout: calculateTimeout(duplicatePhoneWarningMessage) });
					} else {
						this.toast.success(`Contact ${this.isEditing ? "updated" : "created"} successfully.`, {
							timeout: calculateTimeout(`Contact ${this.isEditing ? "updated" : "created"} successfully.`)
						});
					}
				} catch (e) {
					const errorMessage = getMessageFromError(e, couldNot(this.isEditing ? "update the contact" : "add the contact"));
					const duplicate = !this.isEditing ? this.parseDuplicateError(errorMessage) : null;
					if (duplicate) {
						const sessionAtLookupStart = this.sessionId;
						const existing = await this.findExistingDuplicate(duplicate.value);
						// Drop stale lookup if dialog was reopened mid-flight.
						if (this.sessionId !== sessionAtLookupStart) return;
						if (existing) {
							this.duplicateMatch = existing;
							this.duplicateMatchedField = duplicate.field;
							this.mode = "duplicate-confirm";
							return;
						}
					}
					console.error(e, {
						tags: {
							method: "agree",
							file: "CreateCustomerDialog"
						}
					});
					this.toast.error(errorMessage, { timeout: calculateTimeout(errorMessage) });
				} finally {
					this.creating = false;
				}
			},
			parseDuplicateError(message) {
				// Use BE's matched value (not form state): BE checks email before phone,
				// so existing-email + new-phone would otherwise be looked up by phone.
				if (typeof message !== "string") return null;
				const match = message.match(/A contact with the (email|phone number) (.+?) already exists\./);
				if (!match) return null;
				return { field: match[1], value: match[2] };
			},
			async findExistingDuplicate(lookup) {
				if (!lookup) return null;
				try {
					const result = await api.searchCustomers(lookup);
					return result?.rows?.[0] || null;
				} catch (lookupError) {
					console.error(lookupError, {
						tags: { method: "findExistingDuplicate", file: "CreateCustomerDialog" }
					});
					return null;
				}
			},
			resetDuplicateConfirm() {
				this.mode = "form";
				this.duplicateMatch = null;
				this.duplicateMatchedField = "";
			},
			cancel() {
				if (this.mode === "duplicate-confirm") {
					this.resetDuplicateConfirm();
					return;
				}
				// Bump sessionId so an in-flight findExistingDuplicate skips its state writes.
				this.sessionId++;
				this.resetForm("");
				this.dialog = false;
				this.creating = false;
				this.resolve(false);
				this.$emit("createCustomerDialogClosed");
			},
			pasteEvent(event) {
				this.phoneNumber = getTrimmedPhoneFromClipboard(event);
			},
			phoneNumberChanged(number, phoneObject) {
				if (this.validPhone && phoneObject && phoneObject.formatted) {
					this.$nextTick(() => {
						this.phoneNumber = phoneObject.formatted;
					});
				}
			},
			validatePhoneNumber(args) {
				this.validPhone = args.valid;
			},
			phoneNumberBlur() {
				this.showPhoneNumberFieldErrors = true;
			},
			phoneNumberFocus() {
				this.showPhoneNumberFieldErrors = false;
			}
		}
	};
</script>

<style scoped>
	::v-deep .vti__dropdown-list {
		top: auto;
		bottom: 100%;
	}
</style>
